import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Box,
  Button,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import { Delete, Edit, ArrowBack } from '@mui/icons-material';
import FormDestaque from '../../components/Instituicoes/FormDestaque';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import { getDestaques, excluirDestaque } from '../../services/destaqueService';

const GerenciarDestaquesPage = () => {
  const { instituicaoId } = useParams();
  const [destaques, setDestaques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const { adicionarNotificacao } = useNotificacao();

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const fetchDestaques = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getDestaques(instituicaoId);
      setDestaques(data);
      setError(null);
    } catch (error) {
      console.error('Erro ao buscar destaques:', error);
      setError('Erro ao carregar destaques. Por favor, tente novamente.');
      adicionarNotificacao('Erro ao carregar destaques.', 'error');
    } finally {
      setLoading(false);
    }
  }, [instituicaoId, adicionarNotificacao]);

  useEffect(() => {
    fetchDestaques();
  }, [fetchDestaques]);

  const handleFormSuccess = () => {
    showSnackbar('Destaque salvo com sucesso!');
    fetchDestaques();
  };

  const handleExcluirDestaque = async (destaqueId) => {
    if (window.confirm('Tem certeza que deseja excluir este destaque?')) {
      try {
        await excluirDestaque(instituicaoId, destaqueId);
        showSnackbar('Destaque excluído com sucesso');
        adicionarNotificacao('Destaque excluído com sucesso.', 'success');
        await fetchDestaques();
      } catch (error) {
        console.error('Erro ao excluir destaque:', error);
        showSnackbar('Erro ao excluir destaque', 'error');
        adicionarNotificacao('Erro ao excluir destaque.', 'error');
      }
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Cabeçalho com botão voltar */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          component={Link}
          to="/dashboard"
        >
          Voltar ao Dashboard
        </Button>
        <Typography variant="h4">
          Gerenciar Destaques
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        <FormDestaque
          instituicaoId={instituicaoId}
          onSuccess={handleFormSuccess}
        />
      </Box>

      <Grid container spacing={2}>
        {destaques.map((destaque) => (
          <Grid item xs={12} sm={6} md={4} key={destaque.id}>
            <Card>
              <CardContent>
                <Typography variant="h6">{destaque.titulo}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {destaque.descricao}
                </Typography>
                {destaque.imagemUrl && (
                  <Box sx={{ mt: 2 }}>
                    <img 
                      src={`${process.env.REACT_APP_API_URL}/${destaque.imagemUrl}`}
                      alt={destaque.titulo}
                      style={{ width: '100%', height: 'auto', borderRadius: '4px' }}
                    />
                  </Box>
                )}
                {destaque.link && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    Link: {destaque.link}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <IconButton
                  color="primary"
                  onClick={() => console.log('Editar:', destaque.id)}
                >
                  <Edit />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => handleExcluirDestaque(destaque.id)}
                >
                  <Delete />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GerenciarDestaquesPage;